import styled from 'styled-components';
import {Link } from 'react-router-dom';

export const SignupPageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: calc(100vh - ${({theme }) => theme.spacing.xxl} * 2);
  background-color: ${({theme }) => theme.colors.light};
  padding: ${({theme }) => theme.spacing.md};

  @media (max-width: ${({theme }) => theme.breakpoints.md}) {
    padding: ${({theme }) => theme.spacing.sm};
  }
`;

export const SignupForm = styled.form`
  background-color: ${({theme }) => theme.colors.white};
  padding: ${({theme }) => theme.spacing.xl};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  width: 100%;
  max-width: 400px;
  display: flex;
  flex-direction: column;
  gap: ${({theme }) => theme.spacing.md};

  @media (max-width: ${({theme }) => theme.breakpoints.md}) {
    padding: ${({theme }) => theme.spacing.lg};
    gap: ${({theme }) => theme.spacing.sm};
  }
`;

export const Title = styled.h2`
  text-align: center;
  color: ${({theme }) => theme.colors.primary};
  margin-bottom: ${({theme }) => theme.spacing.md};
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme }) => theme.spacing.xs};
`;

export const Label = styled.label`
  font-size: 0.9rem;
  color: ${({theme }) => theme.colors.text};
  font-weight: bold;
`;

export const Input = styled.input`
  padding: ${({theme }) => theme.spacing.sm};
  border: 1px solid ${({theme }) => theme.colors.textLight};
  border-radius: 4px;
  font-size: 1rem;
  outline: none;

  &:focus {
    border-color: ${({theme }) => theme.colors.primary};
    box-shadow: 0 0 0 2px rgba(${({theme }) => theme.colors.primary}, 0.2);
  }
`;

export const Button = styled.button`
  padding: ${({theme }) => theme.spacing.md};
  background-color: ${({theme }) => theme.colors.primary};
  color: ${({theme }) => theme.colors.white};
  border: none;
  border-radius: 4px;
  font-size: 1.1rem;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.2s ease-in-out;

  &:hover {
    background-color: ${({theme }) => theme.colors.secondary};
  }

  &:disabled {
    background-color: ${({theme }) => theme.colors.textLight};
    cursor: not-allowed;
  }
`;

export const StyledLink = styled(Link)`
  text-align: center;
  color: ${({theme }) => theme.colors.primary};
  text-decoration: none;
  font-size: 0.9rem;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${({theme }) => theme.colors.secondary};
  }
`;

export const MessageContainer = styled.div<{type: 'error' | 'success' }>`
  padding: ${({theme }) => theme.spacing.sm};
  border-radius: 4px;
  text-align: center;
  font-size: 0.9rem;
  margin-bottom: ${({theme }) => theme.spacing.md};
  color: ${({type, theme }) => (type === 'error' ? theme.colors.error : theme.colors.success)};
  background-color: ${({type, theme }) => (type === 'error' ? theme.colors.error : theme.colors.success)};
  border: 1px solid ${({type, theme }) => (type === 'error' ? theme.colors.error : theme.colors.success)};
`;

export const TabContainer = styled.div`
  display: flex;
  margin-bottom: ${({theme }) => theme.spacing.md};
  border-bottom: 1px solid ${({theme }) => theme.colors.textLight};
`;

export const Tab = styled.button<{active: boolean }>`
  flex: 1;
  padding: ${({theme }) => theme.spacing.sm};
  border: none;
  background-color: transparent;
  cursor: pointer;
  font-size: 1rem;
  font-weight: ${({active }) => (active ? 'bold' : 'normal')};
  color: ${({active, theme }) => (active ? theme.colors.primary : theme.colors.textLight)};
  border-bottom: ${({active, theme }) => (active ? `2px solid ${theme.colors.primary}` : 'none')};
  transition: all 0.2s ease-in-out;

  &:hover {
    color: ${({theme }) => theme.colors.primary};
  }
`;

export const SocialLoginButtons = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({theme }) => theme.spacing.sm};
  margin-top: ${({theme }) => theme.spacing.md};
  padding-top: ${({theme }) => theme.spacing.md};
  border-top: 1px solid ${({theme }) => theme.colors.light};
`;

export const SocialButton = styled(Button)`
  background-color: #4285f4;
  &:hover {
    background-color: #357ae8;
  }
  &.facebook {
    background-color: #1877f2;
    &:hover {
      background-color: #166fe5;
    }
  }
`;

export const RecaptchaContainer = styled.div`
  margin-top: ${({theme }) => theme.spacing.md};
  display: flex;
  justify-content: center;
`;

export const SubscriptionInfo = styled.div`
  margin: 20px 0;
  padding: 15px;
  background-color: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;

  h3 {
    margin-top: 0;
    margin-bottom: 10px;
    color: #2c3e50;
    font-size: 1.2rem;
  }

  p {
    margin-bottom: 15px;
    color: #495057;
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

export const PaymentOptions = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 15px;
  flex-wrap: wrap;
`;

export const PaymentOption = styled.div<{active: boolean }>`
  flex: 1;
  min-width: 120px;
  padding: 12px;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? '#3498db' : '#e9ecef'};
  background-color: ${props => props.active ? '#f0f8ff' : 'white'};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;

  &:hover {
    border-color: ${props => props.active ? '#3498db' : '#ced4da'};
    background-color: ${props => props.active ? '#f0f8ff' : '#f8f9fa'};
  }

  span {
    margin-top: 8px;
    font-weight: ${props => props.active ? '600' : '500'};
    color: ${props => props.active ? '#3498db' : '#495057'};
    font-size: 0.9rem;
  }
`;

export const PaymentLogo = styled.img`
  width: 40px;
  height: 40px;
  object-fit: contain;
`;